import { DB_TYPE } from '@/common/define/common.define';
import type { DBType } from '@/common/utils';
import type { MetricStatRow } from '@/database/utils/metric.types';

export const ADDITIONAL_STATS_DATA_ID: Record<string, string> = {
  ORACLE_WAIT: 'db_oracle_wait_class',
  ORACLE_TIME: 'db_oracle_time_model',
  ALTIBASE_WAIT: 'db_altibase_wait_class',
  TIBERO_WAIT: 'db_tibero_wait_class',
};

export const MULTI_STATS_DATA_ID: Record<string, string> = {
  CLICKHOUSE_IO: 'db_clickhouse_io',
  POSTGRESQL_IO: 'db_postgresql_io',
  MYSQL_IO: 'db_mysql_io',
  POSTGRESQL_DML: 'db_postgresql_dml',
  ALERT: 'db_alert_history_metric',
  SINGLESTORE_SUCCESSFUL_QUERIES: 'db_singlestore_successful_queries',
  COMMON_DISK_IO_BYTES_PER_SEC: 'db_common_disk_io_bytes_per_sec',
  COMMON_DISK_IO_REQUEST_PER_COUNT: 'db_common_disk_io_request_per_count',
};

export const ALERT_STATS: Required<Omit<MetricStatRow, 'children'>> = {
  rowKey: MULTI_STATS_DATA_ID.ALERT,
  dataIds: ['alert_critical_count', 'alert_warning_count', 'alert_normal_count'],
  displayName: 'Alarm',
  unit: 'count',
  summaryType: ['sum'],
};

const COMMON_ADDITIONAL_STATS: MetricStatRow[] = [
  {
    rowKey: MULTI_STATS_DATA_ID.COMMON_DISK_IO_BYTES_PER_SEC,
    displayName: 'Disk I/O (bytes/sec)',
    dataIds: ['db_os.disk_bytes_in', 'db_os.disk_bytes_out'],
    summaryType: ['avg', 'max'],
  },
  {
    rowKey: MULTI_STATS_DATA_ID.COMMON_DISK_IO_REQUEST_PER_COUNT,
    displayName: 'Disk I/O (requests/sec)',
    dataIds: ['db_os.disk_request_in', 'db_os.disk_request_out'],
    summaryType: ['avg', 'max'],
  },
  ALERT_STATS,
];

export const ADDITIONAL_STATS: Partial<Record<NonNullable<DBType>, MetricStatRow>> = {
  [DB_TYPE.ORACLE]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: ADDITIONAL_STATS_DATA_ID.ORACLE_WAIT,
        dataIds: [ADDITIONAL_STATS_DATA_ID.ORACLE_WAIT],
        displayName: 'Wait Class',
        summaryType: ['current'],
      },
      {
        rowKey: ADDITIONAL_STATS_DATA_ID.ORACLE_TIME,
        dataIds: [ADDITIONAL_STATS_DATA_ID.ORACLE_TIME],
        displayName: 'Time Model',
        summaryType: ['current'],
      },
      ...COMMON_ADDITIONAL_STATS,
    ],
  },
  [DB_TYPE.ALTIBASE]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: ADDITIONAL_STATS_DATA_ID.ALTIBASE_WAIT,
        dataIds: [ADDITIONAL_STATS_DATA_ID.ALTIBASE_WAIT],
        displayName: 'Wait Class',
        summaryType: ['current'],
      },
      ...COMMON_ADDITIONAL_STATS,
    ],
  },
  [DB_TYPE.TIBERO]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: ADDITIONAL_STATS_DATA_ID.TIBERO_WAIT,
        dataIds: [ADDITIONAL_STATS_DATA_ID.TIBERO_WAIT],
        displayName: 'Wait Class',
        summaryType: ['current'],
      },
      ...COMMON_ADDITIONAL_STATS,
    ],
  },
  [DB_TYPE.CLICKHOUSE]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: MULTI_STATS_DATA_ID.CLICKHOUSE_IO,
        displayName: 'Logical + Physical I/O',
        dataIds: [
          'db_clickhouse_readbufferfromfiledescriptorread',
          'db_clickhouse_writebufferfromfiledescriptorwrite',
        ],
        summaryType: ['delta'],
      },
      ...COMMON_ADDITIONAL_STATS,
    ],
  },
  [DB_TYPE.POSTGRESQL]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: MULTI_STATS_DATA_ID.POSTGRESQL_IO,
        displayName: 'Logical + Physical I/O',
        dataIds: ['db_postgresql_blkshit', 'db_postgresql_blksread'],
        summaryType: ['avg', 'max'],
      },
      {
        rowKey: MULTI_STATS_DATA_ID.POSTGRESQL_DML,
        displayName: 'Dml Rows',
        dataIds: [
          'db_postgresql_tupreturned',
          'db_postgresql_tupfetched',
          'db_postgresql_tupinserted',
          'db_postgresql_tupupdated',
          'db_postgresql_tupdeleted',
        ],
        summaryType: ['avg', 'max'],
      },
      ...COMMON_ADDITIONAL_STATS,
    ],
  },
  [DB_TYPE.MYSQL]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: MULTI_STATS_DATA_ID.MYSQL_IO,
        displayName: 'Logical + Physical I/O',
        dataIds: ['db_mysql_innodb_buffer_pool_reads', 'db_mysql_innodb_buffer_pool_read_requests'],
        summaryType: ['avg', 'max'],
      },
      ...COMMON_ADDITIONAL_STATS,
    ],
  },
  [DB_TYPE.SQLSERVER]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: COMMON_ADDITIONAL_STATS,
  },
  [DB_TYPE.REDSHIFT]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [ALERT_STATS],
  },
  [DB_TYPE.SINGLESTORE]: {
    rowKey: 'additional_stats',
    displayName: 'Additional Stats',
    summaryType: [],
    children: [
      {
        rowKey: MULTI_STATS_DATA_ID.SINGLESTORE_SUCCESSFUL_QUERIES,
        displayName: 'Successful Queries',
        dataIds: [
          'db_singlestore_successful_read_queries',
          'db_singlestore_successful_write_queries',
        ],
        summaryType: ['current'],
      },
      ALERT_STATS,
    ],
  },
};
